<template>
  <div>
    <v-data-table v-model="selectedItems"
                  :items="sortedQuestions"
                  :headers="headers"
                  :page="currentPage"
                  :must-sort="!searchQuery"
                  :sort-by="sort"
                  :return-object="true"
                  mobile-breakpoint="md"
                  :items-per-page="itemsPerPage"
                  :show-select="isCurrentUserPm"
                  class="questionAnswerDataTable"
                  @update:sort-by="onSortChange"
                  @click:row="onRowClick"
    >
      <template #no-data>
        {{ $t('project.question-answer.QuestionAnswerDataTable.noResults') }}
      </template>

      <template #item.id="{item}">
        <p class="text--medium">
        {{ `#${item.idInRoom}` }}
        </p>
      </template>

      <template #item.question="{item}">
        <div class="questionAnswerDataTable-description d-inline-block"
             v-html="item.description"
        ></div>
        <div class="d-flex align-center">
          <div class="mr-5">
            <v-chip v-for="category in item.categories"
                    :key="category.id"
                    size="small"
                    class="mr-1"
            >
              {{ category.name }}
            </v-chip>
          </div>
        </div>
      </template>
      <template #item.reference="{item}">
        <AppTooltip v-if="item.file" :max-width="300">
          <template #activator="{ props }">
            <div class="questionAnswerDataTable-reference" v-bind="props">
              <div class="questionAnswerDataTable-reference--text">
                <span v-if="item.file.numbering">{{ item.file.numbering }}</span>
                {{ item.file.basename }}
              </div>
            </div>
          </template>
          <span>
            <span v-if="item.file.numbering">{{ item.file.numbering }}</span>
            {{ item.file.basename }}
          </span>
        </AppTooltip>

        <AppTooltip v-if="item.directory" :max-width="300">
          <template #activator="{ props }">
            <div class="questionAnswerDataTable-reference" v-bind="props">
              <div class="questionAnswerDataTable-reference--text">
                <span v-if="item.directory.numbering">{{ item.directory.numbering }}</span>
                {{ item.directory.name }}
              </div>
            </div>
          </template>
          <span>
            <span v-if="item.directory.numbering">{{ item.directory.numbering }}</span>
            {{ item.directory.name }}
          </span>
        </AppTooltip>
      </template>
      <template #item.author="{item}">
        {{ item.userName }}
      </template>

      <template #item.createdAt="{item}">
        <DateDisplay :value="item.createdAt"/>
      </template>

      <template #item.lastUpdate="{item}">
        <DateDisplay v-if="Date.parse(item.lastUpdate)" :value="item.lastUpdate" />
        <p class="text--medium" v-else>{{ item.lastUpdate }}</p>
      </template>
      <template #item.status="{item}">
        <div class="pl-4">
          <QuestionAnswerStatusIcon :status="item.status"/>
        </div>
      </template>

      <template #item.share="{item}">
        <div style="max-width: 120px">
          <RightsDisplayButton :rights="item.rights" @click.stop="onShareButtonClick(item)" />
        </div>
      </template>

      <template #item.actions="{item}">
        <div class="text-right">
          <AppButton v-if="item.status !== QA_STATUS.VALIDATED"
                      color="primary"
                      @click="onRowClick(item)"
          >
            {{ $t('project.question-answer.QuestionAnswerDataTable.answer') }}
          </AppButton>
        </div>
      </template>

      <template #item.actionsDelete="{item}">
        <QuestionAnswerDataTableItemMenu
          v-if="isCurrentUserPm || item.rights.canWrite || item.userId === currentUserId"
          :item="item"
        />
      </template>
      <template #bottom>
        <div class="d-flex justify-end mt-4">
          <AppPagination v-model:current-page="currentPage"
                         v-model:page-size="itemsPerPage"
                         :total="sortedQuestions.length"
          />
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import { mapGetters, mapState, useStore } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import AppButton from '@/common/buttons/AppButton.vue'
import DateDisplay from '@/common/DateDisplay.vue'
import AppPagination from '@/common/pagination/AppPagination.vue'
import { usePagination } from '@/common/pagination/usePagination'
import RightsDisplayButton from '@/common/users/RightsDisplayButton.vue'
import { QA_STATUS } from '@/common/utils/qa-status'
import QuestionAnswerStatusIcon from '@/project/question-answer/QuestionAnswerStatusIcon.vue'
import { QUESTION_ANSWER_ITEM_ROUTE_NAME } from '@/router'

import QuestionAnswerDataTableItemMenu from './QuestionAnswerDataTableItemMenu.vue'

dayjs.extend(isBetween)
dayjs.extend(isSameOrAfter)

export default {
  name: 'QuestionAnswerDataTable',
  components: {
    AppButton,
    AppPagination,
    AppTooltip,
    DateDisplay,
    QuestionAnswerStatusIcon,
    RightsDisplayButton,
    QuestionAnswerDataTableItemMenu,
  },
  setup: () => {
    const store = useStore()
    const { currentPage, itemsPerPage } = usePagination({
      currentUserId: store.getters['user/currentUserId'],
      userSettingsKey: 'qa-items-per-page',
    })

    return {
      currentPage,
      itemsPerPage,
    }
  },
  props: {
    searchQuery: {
      type: String,
      required: true,
    },
    selectedQuestions: {
      type: Array,
      required: true,
    },
  },
  data () {
    return {
      QA_STATUS,
      headers: [
        {
          title: this.$t('project.question-answer.QuestionAnswerDataTable.headers.id'),
          value: 'id',
          sortable: true,
        },
        {
          title: this.$t('project.question-answer.QuestionAnswerDataTable.headers.question'),
          value: 'question',
          sortable: true,
        },
        {
          title: this.$t('project.question-answer.QuestionAnswerDataTable.headers.reference'),
          value: 'reference',
          sortable: true,
        },
        {
          title: this.$t('project.question-answer.QuestionAnswerDataTable.headers.author'),
          value: 'author',
          sortable: true,
        },
        {
          title: this.$t('project.question-answer.QuestionAnswerDataTable.headers.sharing'),
          value: 'share',
          sortable: true,
        },
        {
          title: this.$t('project.question-answer.QuestionAnswerDataTable.headers.createdAt'),
          value: 'createdAt',
          sortable: true,
        },
        {
          title: this.$t('project.question-answer.QuestionAnswerDataTable.headers.latestAddition'),
          value: 'lastUpdate',
          sortable: true,
        },
        {
          title: this.$t('project.question-answer.QuestionAnswerDataTable.headers.status'),
          value: 'status',
          sortable: true,
        },
        {
          value: 'actions',
          sortable: false,
        },
        {
          value: 'actionsDelete',
          sortable: false,
        },
      ],
      sort: [{ key: 'id', order: 'asc' }],
    }
  },
  computed: {
    ...mapState('questionAnswer', ['filters']),
    ...mapGetters('questionAnswer', ['questions']),
    ...mapGetters('user', ['currentUserId']),
    ...mapGetters('room', ['isCurrentUserPm']),
    selectedItems: {
      get () {
        return this.selectedQuestions
      },
      set (value) {
        this.$emit('update:selected-questions', value)
      },
    },
    filteredQuestions () {
      let filteredQuestions = this.questions
      const questionIdMatchSearchQueryFn = question => question.idInRoom.toString() === this.searchQuery
      if (this.searchQuery) {
        filteredQuestions = filteredQuestions.filter(q => {
          return questionIdMatchSearchQueryFn(q) ||
            q.description.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
            q.userName.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
            (q.file && q.file.basename.toLowerCase().includes(this.searchQuery.toLowerCase())) ||
            q.categories.some(category => category.name.toLowerCase().includes(this.searchQuery.toLowerCase()))
        })

        if (filteredQuestions.find(elem => elem.id.toString().includes(this.searchQuery))) {
          filteredQuestions = filteredQuestions.sort((a, b) => {
            if (!a.id.toString().includes(this.searchQuery) && b.id.toString().includes(this.searchQuery)) return 1
            else if (a.id.toString().includes(this.searchQuery) && !b.id.toString().includes(this.searchQuery)) return -1
            else return 0
          })
        }
      }

      const categoriesFilters = this.filters.filter(filter => filter.category.key === 'categories')
      if (categoriesFilters.length > 0) {
        const categoryMatchFilter = category => categoriesFilters.some(categoryFilter => categoryFilter.value.key === category.id)
        filteredQuestions = filteredQuestions.filter(q => q.categories.some(categoryMatchFilter))
      }

      const questionersFilters = this.filters.filter(filter => filter.category.key === 'questioners')
      if (questionersFilters.length > 0) {
        filteredQuestions = filteredQuestions.filter(q => questionersFilters.some(filter => filter.value.key === q.userName))
      }

      const createdAtFilter = this.filters.find(filter => filter.category.key === 'createdAt')
      if (createdAtFilter) {
        const filterFrom = createdAtFilter.value.key[0]
        const filterTo = createdAtFilter.value.key[1]

        if (filterFrom && !filterTo) {
          filteredQuestions = filteredQuestions.filter(q => {
            const qDate = q.createdAt
            return dayjs(qDate).isSameOrAfter(dayjs(filterFrom))
          })
        } else if (filterFrom && filterTo) {
          filteredQuestions = filteredQuestions.filter(q => {
            const qDate = q.createdAt
            return dayjs(qDate).isBetween(dayjs(filterFrom), dayjs(filterTo), 'day', '[]')
          })
        }
      }

      const lastUpdateFilter = this.filters.find(filter => filter.category.key === 'lastUpdate')
      if (lastUpdateFilter) {
        const filterFrom = lastUpdateFilter.value.key[0]
        const filterTo = lastUpdateFilter.value.key[1]

        if (filterFrom && !filterTo) {
          filteredQuestions = filteredQuestions.filter(q => {
            const qDate = q.lastUpdate
            return dayjs(qDate).isSameOrAfter(dayjs(filterFrom))
          })
        } else if (filterFrom && filterTo) {
          filteredQuestions = filteredQuestions.filter(q => {
            const qDate = q.lastUpdate
            return dayjs(qDate).isBetween(dayjs(filterFrom), dayjs(filterTo), 'day', '[]')
          })
        }
      }

      const referencesFilters = this.filters.filter(filter => filter.category.key === 'references')
      if (referencesFilters.length > 0) {
        filteredQuestions = filteredQuestions.filter(q => referencesFilters.some(filter => filter.value.key === q.file?.id))
      }

      const statusFilters = this.filters.filter(filter => filter.category.key === 'status')
      if (statusFilters.length > 0) {
        filteredQuestions = filteredQuestions.filter(q => statusFilters.some(filter => filter.value.key === q.status))
      }

      return filteredQuestions
    },
    sortedQuestions () {
      const items = [...this.filteredQuestions]
      const [sort] = this.sort
      const sortDesc = sort.order === 'desc'
      const exactIdIndex = items.findIndex(elem => elem.idInRoom.toString() === this.searchQuery)
      const exactIdItem = items[exactIdIndex]

      if (exactIdIndex !== -1) items.splice(exactIdIndex, 1)
      items.sort((a, b) => {
        if (sort.key === 'id') {
          if (sortDesc) {
            return b.idInRoom - a.idInRoom
          } else {
            return a.idInRoom - b.idInRoom
          }
        }
        if (sort.key === 'question') {
          if (sortDesc) {
            return b.description.localeCompare(a.description)
          } else {
            return a.description.localeCompare(b.description)
          }
        }
        if (sort.key === 'reference') {
          if (sortDesc) {
            return b.file?.basename?.localeCompare(a.file?.basename)
          } else {
            return a.file?.basename?.localeCompare(b.file?.basename)
          }
        }
        if (sort.key === 'author') {
          if (sortDesc) {
            return b.userName.localeCompare(a.userName)
          } else {
            return a.userName.localeCompare(b.userName)
          }
        }
        if (sort.key === 'createdAt') {
          if (sortDesc) {
            return dayjs(a.createdAt).isBefore(dayjs(b.createdAt)) ? 1 : -1
          } else {
            return dayjs(a.createdAt).isBefore(dayjs(b.createdAt)) ? -1 : 1
          }
        }
        if (sort.key === 'lastUpdate') {
          if (sortDesc) {
            return dayjs(a.lastUpdate).isBefore(dayjs(b.lastUpdate)) ? 1 : -1
          } else {
            return dayjs(a.lastUpdate).isBefore(dayjs(b.lastUpdate)) ? -1 : 1
          }
        }
        if (sort.key === 'status') {
          if (sortDesc) {
            return this.$t(`common.qaStatus.${b.status}`).localeCompare(this.$t(`common.qaStatus.${a.status}`))
          } else {
            return this.$t(`common.qaStatus.${a.status}`).localeCompare(this.$t(`common.qaStatus.${b.status}`))
          }
        }
      })
      if (exactIdIndex !== -1) items.unshift(exactIdItem)
      return items
    },
  },
  methods: {
    onRowClick (item, vuetifyRowObject = null) {
      // Badly documented. If we use the @click:row handler, we have the original event and the row object containing the item
      // As a second argument. So we'll only have the second argument when using the direct Vuetify event.
      // Source: https://github.com/vuetifyjs/vuetify/blob/2368668d49b322a1ac7aa5a9d602676affdef9b2/packages/api-generator/src/locale/en/VDataTable.json#L69
      if (vuetifyRowObject) {
        item = vuetifyRowObject.item
      }
      this.$router.push({
        name: QUESTION_ANSWER_ITEM_ROUTE_NAME,
        params: {
          questionId: item.id,
        },
      })
    },
    onShareButtonClick (item) {
      this.$router.push({
        name: QUESTION_ANSWER_ITEM_ROUTE_NAME,
        params: {
          questionId: item.id,
        },
        query: {
          openSharePane: true,
        },
      })
    },
    onSortChange (sort) {
      this.sort = sort
    },
  },
}
</script>

<style scoped lang="scss">
.questionAnswerDataTable {
  background-color: transparent;

  :deep(table) {
    border-spacing: 0 6px;

    thead {
      th {
        font-size: 14px;
        font-weight: 500;
        text-transform: uppercase;
        color: rgb(var(--v-theme-accent));
        border: none !important;
        white-space: nowrap;
      }
    }

    tbody {
      tr {
        background: rgb(var(--v-theme-white));
        transition: box-shadow .5s;

        &:hover {
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
        }
      }

      td {
        border: none !important;
        padding-top: 16px !important;
        padding-bottom: 16px !important;

        &:last-child {
          border-top-right-radius: 6px;
          border-bottom-right-radius: 6px;
        }
      }
    }
  }
}
</style>
